import {Language, LegalDocument} from "@/api/data-contracts";
import {legalDocumentApi} from "@/boxhub-api";

/**
 * A loaded legal document: its HTML content plus the language that content is actually written in.
 *
 * `effectiveLanguage` differs from the requested language whenever the server applied the German
 * fallback for a language without a maintained value — callers need it to label the rendered content
 * correctly (`lang` attribute) instead of inheriting the UI language.
 */
export interface LegalDocumentContent {
    content: string;
    effectiveLanguage: Language;
}

/**
 * Load state of a legal document's content: request in flight, content rendered, or load failed.
 *
 * Shared by every view that renders a legal document (public info pages and the registration
 * agreement steps) so the three states stay named identically across them. The render path itself is
 * deliberately NOT shared: the info page owns its request and reloads on a language switch, whereas
 * an agreement step receives already-loaded content as a prop and couples it to a scroll-to-accept
 * gate — a common hook would have to carry both shapes and would not simplify either.
 */
export type LegalDocumentLoadState = "loading" | "loaded" | "error";

/**
 * Loads the HTML content of a legal document (imprint, privacy policy, terms and conditions, ...)
 * in the requested language from `GET /api/legal-documents/{document}/{language}`. The endpoint is
 * anonymously reachable, so no session is required.
 *
 * Never throws: a failed request (network error, 4xx/5xx, or a response without content) is logged
 * and answered with `undefined`, which lets callers render their own load-failure message instead of
 * having to handle an exception. The German fallback for a language without a maintained value is
 * applied server-side — the returned `effectiveLanguage` names the language the content is written in
 * and falls back to the requested language if the response does not state it.
 *
 * Language signals on this request: the `{language}` PATH SEGMENT is the only authoritative source for
 * the document's language — the controller passes the bound path enum straight to the service. The
 * `?lang=` query param that `registerLangInterceptor` adds to every client request is read by the
 * backend `BoxHubLocaleResolver` and only localizes message resolution (i.e. the text of a 400/404
 * error response). A transient mismatch during a language switch (`.../GERMAN?lang=en`) therefore
 * cannot yield content in an unintended language; it can at most localize an error message differently.
 * The interceptor is deliberately kept for this client so those error texts stay localized.
 *
 * @param document - The legal document to load
 * @param language - The language to request the document in
 * @returns The document's content and effective language, or `undefined` when it could not be loaded
 */
export async function fetchLegalDocumentContent(
    document: LegalDocument,
    language: Language
): Promise<LegalDocumentContent | undefined> {
    try {
        const {data} = await legalDocumentApi.getLegalDocument(document, language);
        if (data.content === undefined) {
            return undefined;
        }
        return {content: data.content, effectiveLanguage: data.effectiveLanguage ?? language};
    } catch (error: unknown) {
        console.error(`Failed to fetch legal document ${document} in ${language}`, error);
        return undefined;
    }
}
